import { CheckCircle2, Smartphone } from "lucide-react";
import { V2Shell } from "@/v2/components/V2Shell";
import { Button } from "@/v2/components/ui/button";

type Props = {
  onDisconnect: () => void;
  /**
   * Task 9's useSecondScreenSession().connected: whether THIS desktop's own
   * stream to the server is up right now, not whether the phone is still
   * paired. False while it reconnects (a wifi blip, the laptop sleeping, an
   * ECS task cycling on deploy), which can happen without the pairing ever
   * having ended.
   *
   * Showing nothing here is not a safe default: the `revoked` event that
   * would bring this panel back cannot reach a device whose own stream is
   * the thing that broke, so without this line a fan watches an ordinary
   * "Connected" card while the server quietly counts down to revoking their
   * pairing, then sees the panel reappear with no explanation.
   */
  connected: boolean;
  /**
   * The last thing the paired phone asked this screen to do, or null when it
   * has asked for nothing yet (and after a return to the game, which leaves
   * nothing to narrate).
   *
   * Optional so a caller that has not wired up the executor renders exactly
   * the card it always did.
   */
  lastCommand?: { type: string; label: string } | null;
};

/**
 * Sits over the (still-mounted, now inert) Audience Layer panel once a phone
 * has taken it over. Disconnect restores the panel instantly, with chat
 * history and scroll position intact, instead of the layer refetching from
 * scratch.
 */
export function ConnectedToPhoneCard({ onDisconnect, connected, lastCommand }: Props) {
  return (
    <V2Shell>
      <div
        className="flex flex-col items-center rounded-2xl border border-border bg-card text-center"
        style={{ padding: "28px", maxWidth: 300 }}
      >
        <div
          className="relative flex items-center justify-center rounded-full bg-muted"
          style={{ width: 56, height: 56 }}
        >
          <Smartphone className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
          <CheckCircle2
            className="absolute rounded-full bg-card text-green-500"
            style={{ width: 20, height: 20, right: -2, bottom: -2 }}
            aria-hidden="true"
          />
        </div>

        <div className="flex flex-col gap-1" style={{ marginTop: "16px" }}>
          <span className="text-base font-semibold text-foreground">Connected to Your Phone</span>
          {/* Quiet, not alarming: this is expected during a brief drop and
              resolves on its own once the stream reconnects. */}
          {!connected && <span className="text-xs text-muted-foreground">Reconnecting…</span>}
        </div>

        <p className="text-sm text-muted-foreground" style={{ marginTop: "8px" }}>
          The Audience Layer is now active on your phone for this game.
        </p>

        {/*
          Why the video just changed.

          The fan holding the phone knows what they did; anyone else in the
          room is watching a television act on its own. This is already the
          one surface covering the blocked panel, so saying it here costs no
          new furniture on the watch page and nothing appears over the video.
        */}
        {lastCommand && (
          <p
            data-testid="second-screen-activity"
            className="text-sm text-muted-foreground"
            style={{ marginTop: "8px" }}
          >
            {`${lastCommand.label}, sent from your phone`}
          </p>
        )}

        <div
          className="w-full bg-border"
          style={{ height: 1, marginTop: "20px", marginBottom: "20px" }}
        />

        {/*
          The colour is stated here rather than left to the variant.

          `outline` sets a border, a background and a hover, but no resting
          text colour, so this button took whatever the cascade handed it and
          what it got was a blue that belonged to no tenant. Naming the token
          makes it the brand's colour on every tenant and puts it beyond the
          reach of whichever unlayered v1 rule was painting it before.

          --v2-action rather than --v2-primary: they carry the same value (see
          theme-bridge), and this is the token that means "press this to do a
          thing", which is also the one --v2-action-hover pairs with.
        */}
        <Button
          variant="outline"
          className="ring-1 ring-inset ring-border"
          style={{ color: "var(--v2-action)" }}
          onClick={onDisconnect}
        >
          Disconnect
        </Button>
      </div>
    </V2Shell>
  );
}
